import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Trash2, MessageCircle, Send, ShoppingBag } from "lucide-react";
import { useEnquiry } from "@/context/EnquiryContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { wa } from "@/lib/brand";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyBuyer = {
  name: "", company: "", country: "", email: "", phone: "",
  destination_port: "", incoterm: "FOB", packaging: "", delivery_timeline: "", message: "", website: "",
};

const EnquiryCart = () => {
  const { items, updateItem, removeItem, clearCart } = useEnquiry();
  const [buyer, setBuyer] = useState(emptyBuyer);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  const on = (k) => (e) => setBuyer((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!buyer.name || !buyer.company || !buyer.country || !buyer.email || !buyer.phone) {
      toast.error("Please fill your details to submit the enquiry");
      return;
    }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/enquiries`, { ...buyer, items, source: "enquiry-cart", page: window.location.pathname });
      toast.success("Bulk enquiry submitted");
      clearCart();
      navigate(`/enquiry-confirmed/${data.id}`);
    } catch (err) {
      toast.error("Could not submit. Please try again or WhatsApp us.");
    } finally { setBusy(false); }
  };

  const waMessage = () => {
    const lines = items.map((i) => `• ${i.product_name} — ${i.quantity_mt || "?"} MT, ${i.packaging || "packaging TBC"}, ${i.incoterm || ""} ${i.destination_port ? "→ " + i.destination_port : ""}`);
    return wa(`Hello GN Agro Traders, please share a quote for:\n${lines.join("\n")}`);
  };

  return (
    <div>
      <section className="bg-[#004225] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
          <div className="text-[11px] font-eyebrow text-[#D4AF37]"><span className="gold-line mr-3" />B2B Enquiry Cart</div>
          <h1 className="mt-2 font-display text-4xl sm:text-5xl">Your Enquiry</h1>
          <p className="mt-2 text-white/80 text-sm">Assemble multiple commodity lines, add tonnage, packaging and destination — and submit a unified RFQ.</p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {items.length === 0 ? (
          <div className="bg-white border border-[#004225]/10 rounded-sm p-12 text-center">
            <ShoppingBag className="h-8 w-8 text-[#B5942D] mx-auto" />
            <div className="mt-3 font-display text-2xl text-[#004225]">Your enquiry is empty</div>
            <p className="mt-2 text-[13px] text-[#171717]/70">Add products from the catalogue to create a bulk enquiry.</p>
            <Link to="/products" className="mt-5 inline-block rounded-full btn-primary px-6 py-2.5 text-[11px] uppercase tracking-widest font-semibold">Browse Products</Link>
          </div>
        ) : (
          <div className="grid lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-3" data-testid="enquiry-items">
              {items.map((i) => (
                <div key={i.product_id} className="bg-white border border-[#004225]/10 rounded-sm p-5">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <div className="text-[10px] font-eyebrow text-[#B5942D]">{i.category}</div>
                      <div className="font-display text-lg text-[#004225]">{i.product_name}</div>
                    </div>
                    <button onClick={() => removeItem(i.product_id)} data-testid={`remove-${i.product_id}`} className="text-[#171717]/50 hover:text-red-600" aria-label="Remove"><Trash2 className="h-4 w-4" /></button>
                  </div>
                  <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                    <div><Label>Quantity (MT)</Label><Input value={i.quantity_mt} onChange={(e) => updateItem(i.product_id, { quantity_mt: e.target.value })} placeholder="e.g. 50" /></div>
                    <div><Label>Packaging</Label><Input value={i.packaging} onChange={(e) => updateItem(i.product_id, { packaging: e.target.value })} placeholder="e.g. 25 kg PP" /></div>
                    <div>
                      <Label>Incoterm</Label>
                      <Select value={i.incoterm || "FOB"} onValueChange={(v) => updateItem(i.product_id, { incoterm: v })}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="FOB">FOB</SelectItem>
                          <SelectItem value="CFR">CFR</SelectItem>
                          <SelectItem value="CIF">CIF</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div><Label>Destination Port</Label><Input value={i.destination_port} onChange={(e) => updateItem(i.product_id, { destination_port: e.target.value })} placeholder="e.g. Jebel Ali" /></div>
                  </div>
                </div>
              ))}
              <div className="flex flex-wrap gap-3 pt-2">
                <a href={waMessage()} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded-full bg-[#25D366] text-white px-5 py-2.5 text-[11px] font-semibold uppercase tracking-widest">
                  <MessageCircle className="h-4 w-4" /> Send on WhatsApp
                </a>
                <button onClick={clearCart} data-testid="clear-cart" className="rounded-full border border-[#004225]/20 text-[#004225] px-5 py-2.5 text-[11px] font-semibold uppercase tracking-widest hover:bg-[#F7F2E2]">Clear</button>
              </div>
            </div>

            <form onSubmit={submit} className="bg-white border border-[#004225]/10 rounded-sm p-6 h-fit lg:sticky lg:top-24">
              <div className="font-display text-2xl text-[#004225]">Buyer Details</div>
              <p className="text-[12px] text-[#171717]/60">Submit to receive a formal RFQ response.</p>
              <div className="mt-4 space-y-3">
                <div><Label>Full Name *</Label><Input value={buyer.name} onChange={on("name")} data-testid="cart-name" required /></div>
                <div><Label>Company *</Label><Input value={buyer.company} onChange={on("company")} data-testid="cart-company" required /></div>
                <div><Label>Country *</Label><Input value={buyer.country} onChange={on("country")} data-testid="cart-country" required /></div>
                <div><Label>Email *</Label><Input type="email" value={buyer.email} onChange={on("email")} data-testid="cart-email" required /></div>
                <div><Label>Phone / WhatsApp *</Label><Input value={buyer.phone} onChange={on("phone")} data-testid="cart-phone" required /></div>
                <div><Label>Expected Delivery</Label><Input value={buyer.delivery_timeline} onChange={on("delivery_timeline")} placeholder="e.g. within 45 days" /></div>
                <div><Label>Additional Requirements</Label><Textarea rows={3} value={buyer.message} onChange={on("message")} placeholder="Private label, certifications, sample requests…" /></div>
                <input type="text" name="website" value={buyer.website} onChange={on("website")} tabIndex={-1} autoComplete="off" style={{ position: "absolute", left: "-9999px", opacity: 0, height: 0 }} aria-hidden="true" />
                <Button type="submit" disabled={busy} data-testid="submit-bulk-enquiry" className="w-full btn-primary rounded-full uppercase tracking-widest text-[11px]">
                  {busy ? "Submitting…" : (<><Send className="h-4 w-4 mr-2" />Submit Bulk Enquiry</>)}
                </Button>
              </div>
            </form>
          </div>
        )}
      </section>
    </div>
  );
};

export default EnquiryCart;
